import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchClientPortalOrder } from '../api/clientPortalApi';
import { ClientPortalLayout } from '../components/ClientPortalLayout';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { InvoicePaymentDetails } from '../components/InvoicePaymentDetails';
import { BackButton } from '../components/BackButton';
import { formatCurrency, formatDate, formatMoveState } from '../utils/formatters';

function TrackingCell({ trackingRef, trackingUrl }) {
  if (!trackingUrl) {
    return trackingRef || 'Not available';
  }

  return (
    <a href={trackingUrl} target="_blank" rel="noreferrer">
      {trackingRef || '[Tracking link]'}
    </a>
  );
}

export function ClientPortalOrderPage() {
  const { "*": orderName } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['client-portal-order', orderName],
    queryFn: fetchClientPortalOrder,
  });

  const order = data?.result;
  let errorMsg = '';
  if(isError){
    if(error.status === 404){
      errorMsg = 'Order not found';
    }else{
      errorMsg = error.message;
    }
  }

  return (
    <ClientPortalLayout>
      <div className="container">
        {isLoading ? (
          <div className="page-container text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading order details...</p>
          </div>
        ) : isError ? (
          <div className="page-container">
            <Alert variant="danger">{errorMsg}</Alert>
          </div>
        ) : !order ? (
          <div className="page-container">
            <Alert variant="warning">Order not found.</Alert>
          </div>
        ) : (
          <>
            <div className="pt-3">
              <BackButton fallbackUrl="/client/orders" className="mb-2" />
            </div>
            <div className="row"><h2>{order.name}</h2></div>
            <div className="row my-2">
              <div className="col-3">
                <Card>
                  <Card.Header as="h5">Basic info</Card.Header>
                  <Card.Body>
                    <p>Incoterm: <b>{order.incoterm_short || '-'}</b></p>
                    <p>Company: <b>{order.partner?.name || '-'}</b></p>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-9">
                <Card>
                  <Card.Header as="h5">Products</Card.Header>
                  <Table responsive striped className="card-body table product-table mb-0">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Total Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.lines || []).map((line) => (
                        <tr key={line.name}>
                          <th scope="row">
                            {line.product?.printable_default_code && <div className="small text-muted">{line.product.printable_default_code}</div>}
                            {line.product?.name || '-'}
                          </th>
                          <td>{line.product_qty}</td>
                          <td>{formatCurrency(line.price_unit)}</td>
                          <td>{formatCurrency(line.price_subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="fw-bold border-top">
                      <tr>
                        <th scope="row" colSpan={3}>Grand total</th>
                        <td>{formatCurrency(order.amount_total)}</td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card>
              </div>
            </div>
            <div className="row my-2">
              <div className="col-6">
                <Card>
                  <Card.Header as="h5">Shipments</Card.Header>
                  <Table responsive striped className="card-body table delivery-table mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Tracking</th>
                        <th>ETA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.pickings || []).map((delivery) => {
                        const trackingRef = delivery.carrier_tracking_ref === 'False' ? null : delivery.carrier_tracking_ref;
                        const trackingUrl = delivery.carrier_tracking_url_manual === 'False' ? null : delivery.carrier_tracking_url_manual;

                        return (
                          <tr key={delivery.name}>
                            <td><Link to={`/client/deliveries/${delivery.name}`}>{delivery.name}</Link></td>
                            <td>{formatMoveState(delivery.state) || '-'}</td>
                            <td><TrackingCell trackingRef={trackingRef} trackingUrl={trackingUrl} /></td>
                            <td>{formatDate(delivery.scheduled_date)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card>
              </div>
              <div className="col-6">
                <Card>
                  <Card.Header as="h5">Invoices</Card.Header>
                  <Table responsive striped className="card-body table invoice-table mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Date</th>
                        <th>Total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.invoices || []).map((invoice) => (
                        <tr key={invoice.name}>
                          <td>
                            <div className="d-inline-flex align-items-center gap-2">
                              <Link to={`/client/invoices/${invoice.name}`}>{invoice.name}</Link>
                              {invoice.payment_state === 'partial' && (
                                <InvoicePaymentDetails invoiceName={invoice.name} />
                              )}
                              {invoice.download_link && (
                                <a href={invoice.download_link} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                  <Badge bg="success">PDF</Badge>
                                </a>
                              )}
                            </div>
                          </td>
                          <td>{formatDate(invoice.invoice_date)}</td>
                          <td>{formatCurrency(invoice.amount_total_client_signed)}</td>
                          <td><PaymentStatusBadge state={invoice.payment_state} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              </div>
            </div>
            {order.note ? (
              <div className="row my-2">
                <div className="col-12">
                  <Card>
                    <Card.Header as="h5">Note</Card.Header>
                    <Card.Body>
                      <div className="sale-note" dangerouslySetInnerHTML={{ __html: order.note }} />
                    </Card.Body>
                  </Card>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </ClientPortalLayout>
  );
}
